import { useHistory } from 'react-router';
import { Button } from '../../../shared/formElements/button';
import * as Yup from 'yup';
import {
  Form,
  FormContainer,
  FormSectionContainer,
  ButtonGroupContainer,
  SectionContainer,
  LoadingSpinner,
  Line,
  ImageSplitFormSection,
} from '../../../shared/Layout.styles';
import { Markdown } from '../../../shared/markdown';
import { activateRenewedCardConstants } from '../../../utils/constants/activateRenewed';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useContextDispatch, useContextState } from '../../context/context';
import { ActivateRenewedCardInputs } from './sections';
import { OTPModal } from '../../../shared/modal/OTPModal';
import { useState } from 'react';
import { IconSectionCard } from '../../../shared/iconSectionCardSection';
import axios from 'axios';
import { globalConstants } from '../../../utils/constants/global';

const { phoneNumber, activationCode } =
  activateRenewedCardConstants.validations;

export const ActivateRenewedCard = () => {
  const dispatch = useContextDispatch();
  const { loading } = useContextState();
  const [serverErrors, setServerErrors] = useState('');
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [operationId, setOperationId] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');

  const {
    heading,
    subHeading,
    activateCardText,
    sendOTPButtonText,
    otpModalTitle,
    otpModalSubText,
    otpValidityText
  } = activateRenewedCardConstants;

  const { expiredCardMessage } = globalConstants;
  const { push } = useHistory();

  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema) as any,
    shouldFocusError: false,
    defaultValues: {
      activationCode: '',
      phoneNumber: '',
      termsConditionsCheck: false,
    },
  });

  const onInitiateRenewal = async (formData: any) => {
    try {
      dispatch('START_LOADING');
      setServerErrors('');

      const data = {
        eaid: formData.activationCode,
        mobile_number: formData.phoneNumber,
      };

      const response = await axios.post('/InitiateRenewal', data);

      setOperationId(response.data.operation_id);
      setShowOTPModal(true);

    } catch (err: any) {
      console.log('err', err);
      if (err.response?.status === 400) {
        setServerErrors(err.response.data.message);
      } else if (err.response?.status === 410) {
        setServerErrors(expiredCardMessage);
      } else {
        setServerErrors('An error occurred. Please try again.');
      }
      dispatch('STOP_LOADING');
    }
  };

  const onActivateRenewal = async (otpCode: string) => {
    try {
      setModalLoading(true);
      setModalError('');

      const data = {
        eaid: methods.getValues('activationCode'),
        operation_id: operationId,
        security_code: otpCode
      };

      await axios.post('/ActivateRenewal', data);

      setModalLoading(false);
      setShowOTPModal(false);
      dispatch('STOP_LOADING');
      push('/success');

    } catch (err: any) {
      console.log('err', err);
      if (err.response?.status === 400) {
        setModalError(err.response.data.message);
      } else {
        setModalError('Invalid OTP code. Please try again.');
      }
      setModalLoading(false);
      throw err; // Re-throw to let modal handle the error
    }
  };

  const handleCloseModal = () => {
    dispatch('STOP_LOADING')
    setShowOTPModal(false);
    setOperationId('');
    setServerErrors('');
  };

  return (
    <>
      <Line />
      <FormProvider {...(methods as any)}>
        <Form onSubmit={methods.handleSubmit(onInitiateRenewal)}>
          <SectionContainer>
            <Markdown children={heading} />
            <Markdown children={subHeading} />
            <Markdown children={activateCardText} />
            <FormContainer>
              <FormSectionContainer className="activateCardSection">
                <ActivateRenewedCardInputs />
                {serverErrors && (
                  <Markdown
                    children={serverErrors}
                    className="validationText"
                  />
                )}
                <ButtonGroupContainer>
                  <Button
                    type="submit"
                    onClick={() => {
                      methods.handleSubmit(onInitiateRenewal);
                    }}
                  >
                    {loading ? <LoadingSpinner show /> : sendOTPButtonText}
                  </Button>
                </ButtonGroupContainer>
              </FormSectionContainer>
              <FormSectionContainer>
                {process.env.REACT_APP_CARD_OR_ICONS === 'CARD' ? (
                  <ImageSplitFormSection
                    src={process.env.REACT_APP_CARD_FRONT ?? ''}
                  />
                ) : (
                  <IconSectionCard />
                )}
              </FormSectionContainer>
            </FormContainer>
          </SectionContainer>
        </Form>
      </FormProvider>

      <OTPModal
        isOpen={showOTPModal}
        onClose={handleCloseModal}
        onSubmit={onActivateRenewal}
        onCancel={handleCloseModal}
        title={otpModalTitle}
        subtitle={otpModalSubText}
        validityText={otpValidityText}
        loading={modalLoading}
        error={modalError}
      />
    </>
  );
};

const validationSchema = Yup.object().shape({
  activationCode: Yup.string()
    .required(activationCode.required)
    .matches(/^[a-zA-Z0-9]*$/, {
      message: activationCode.format,
      excludeEmptyString: true,
    }),
  phoneNumber: Yup.string()
    .required(phoneNumber.required)
    .matches(/^(\+61|0)[0-9]{9}$/, {
      message: phoneNumber.format,
      excludeEmptyString: true,
    }),
});